// Dust / spam token grouping for the dashboard token lists.
//
// After any on-chain activity (e.g. receiving USDC on Base) the portfolio's
// discovery hints start returning scam airdrop tokens the account technically
// holds (amount > 0) but never asked for — they have no USD price feed and
// render as "SYMBOL  $-" rows. Instead of hiding them outright (a legit new
// token can also lack a price feed for a while), the dashboards collapse them
// behind an expandable "N other tokens" row.
//
// Modeled after upstream Ambire's dust-tokens feature (v6.15.0-era
// `isDustToken`): dust = no USD price OR value below DUST_TOKEN_MAX_USD.
import { formatUnits, ZeroAddress } from 'ethers'

import { PINNED_TOKENS } from '@ambire-common/consts/pinnedTokens'
import { Network } from '@ambire-common/interfaces/network'
import { TokenResult } from '@ambire-common/libs/portfolio/interfaces'

export const DUST_TOKEN_MAX_USD = 0.01

// Local mirror of @ambire-common/libs/portfolio/helpers `getTokenBalanceInUSD`
// (post-simulation amounts are exempted before this runs, so plain `amount` is
// enough). Not imported: that module transitively pulls the whole portfolio
// lib into the UI bundle and trips the stricter extension tsconfig in ts-jest.
const getBalanceUSD = (token: TokenResult): number => {
  const priceIn = Array.isArray(token.priceIn) ? token.priceIn : []
  const usdPrice = priceIn.find((p) => p.baseCurrency === 'usd')?.price || 0
  return parseFloat(formatUnits(token.amount, token.decimals)) * usdPrice
}

// PORTFOLIO_LIB_ERROR_NAMES.PriceFetchError, inlined so this helper doesn't
// pull the whole portfolio lib (and its gecko batcher) into the UI bundle.
const PRICE_FETCH_ERROR_ID = 'PriceFetchError'

const hasRealUSDPrice = (token: TokenResult): boolean => {
  // `priceIn` can arrive as a non-array on the mobile-web worker→page path
  // (iOS Safari); guard like useDashboardAssets does for marketDataIn.
  const priceIn = Array.isArray(token.priceIn) ? token.priceIn : []
  // A 0 price is treated as "no price": the relayer occasionally returns
  // usd: 0 for tokens it can't quote, and a 0-priced holding is
  // indistinguishable from an unpriced one for grouping purposes.
  return priceIn.some((p) => p.baseCurrency === 'usd' && !!p.price)
}

export const isDustToken = (token: TokenResult): boolean => {
  // Natives were always unconditionally shown (portfolio tokenFilter includes
  // them at any amount) — a vanishing gas token would read as a wallet bug.
  if (token.address === ZeroAddress) return false

  // Rewards and vesting tokens should never be grouped as dust.
  if (
    token.flags.rewardsType === 'wallet-rewards' ||
    token.flags.rewardsType === 'wallet-vesting'
  ) {
    return false
  }

  // Mid-simulation tokens must stay visible: sending the full amount makes the
  // post-simulation balance 0, which would otherwise flip the row into dust.
  if (typeof token.amountPostSimulation === 'bigint') return false

  // User-added tokens are tracked intentionally, price feed or not.
  if (token.flags.isCustom) return false

  const isPinned = PINNED_TOKENS.some(
    (pinned) =>
      pinned.chainId === token.chainId &&
      pinned.address.toLowerCase() === token.address.toLowerCase()
  )
  if (isPinned) return false

  return !hasRealUSDPrice(token) || getBalanceUSD(token) < DUST_TOKEN_MAX_USD
}

/**
 * Chain ids (stringified) whose price fetch currently fails. On those networks
 * EVERY token would classify as dust, so grouping must be skipped for them —
 * same guard as upstream's dust feature.
 */
export const getNetworkIdsWithPriceError = (
  balanceAffectingErrors: { id: string; networkNames: string[] }[] | undefined,
  networks: Network[]
): Set<string> => {
  const networkIds = new Set<string>()

  const priceError = (balanceAffectingErrors || []).find(
    (error) => error.id === PRICE_FETCH_ERROR_ID
  )

  priceError?.networkNames.forEach((networkName) => {
    const network = networks.find((n) => n.name === networkName)
    if (network) networkIds.add(network.chainId.toString())
  })

  return networkIds
}

/**
 * Splits an already-filtered/sorted token list into the rows to render and the
 * rows to collapse behind the "N other tokens" summary.
 */
export const splitDustTokens = <T extends TokenResult>(
  tokens: T[],
  networkIdsWithPriceError: Set<string>,
  // Caller-specific exemptions isDustToken can't see (e.g. mobile-web custom
  // tokens are tracked as a controller list, not via token.flags.isCustom).
  isExempt?: (token: T) => boolean
): { visibleTokens: T[]; dustTokens: T[] } => {
  return tokens.reduce(
    (acc, token) => {
      if (
        !isExempt?.(token) &&
        isDustToken(token) &&
        !networkIdsWithPriceError.has(token.chainId.toString())
      ) {
        acc.dustTokens.push(token)
      } else {
        acc.visibleTokens.push(token)
      }
      return acc
    },
    { visibleTokens: [] as T[], dustTokens: [] as T[] }
  )
}
